import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw9204f1Api, F90F, DetailRequest } from '../../../services/pw9204f1Api';

export type Mode = 'browse' | 'add' | 'edit';

export function useTransferForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F90F[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [empId, setEmpId] = useState('');
  const [desc, setDesc] = useState('');
  const [oldDept, setOldDept] = useState('');
  const [oldTitle, setOldTitle] = useState('');
  const [oldGrade, setOldGrade] = useState('');
  const [hireDate, setHireDate] = useState<string | null>(null);
  const [newDept, setNewDept] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newGrade, setNewGrade] = useState('');
  const [effDate, setEffDate] = useState<string | null>(null);
  const [notifyOldMgr, setNotifyOldMgr] = useState(false);
  const [details, setDetails] = useState<DetailRequest[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pw9204f1Api.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setEmpId(current.f90f_11);
      setDesc(current.f90f_13);
      setOldDept(current.f90f_21);
      setOldTitle(current.f90f_22);
      setOldGrade(current.f90f_23);
      setHireDate(current.f90f_24);
      setNewDept(current.f90f_31);
      setNewTitle(current.f90f_32);
      setNewGrade(current.f90f_33);
      setEffDate(current.f90f_38);
      setNotifyOldMgr(current.f90f_43);
      setDetails(
        (current.details || []).map(d => ({
          f90f1_11: d.f90f1_11,
          f90f1_12: d.f90f1_12,
        }))
      );
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add');
    setEmpId('');
    setDesc('');
    setOldDept('');
    setOldTitle('');
    setOldGrade('');
    setHireDate(null);
    setNewDept('');
    setNewTitle('');
    setNewGrade('');
    setEffDate(null);
    setNotifyOldMgr(false);
    setDetails([{ f90f1_11: '', f90f1_12: '' }]);
  };

  const startEdit = () => {
    if (!current) return;
    if (current.f90f_71 === 'Y' || current.f90f_76 === 'Y') {
      message.warning('此異動單已核准，不可修改');
      return;
    }
    setMode('edit');
  };

  const cancel = () => {
    setMode('browse');
    if (current) {
      setEmpId(current.f90f_11);
      setDesc(current.f90f_13);
      setOldDept(current.f90f_21);
      setOldTitle(current.f90f_22);
      setOldGrade(current.f90f_23);
      setHireDate(current.f90f_24);
      setNewDept(current.f90f_31);
      setNewTitle(current.f90f_32);
      setNewGrade(current.f90f_33);
      setEffDate(current.f90f_38);
      setNotifyOldMgr(current.f90f_43);
      setDetails(
        (current.details || []).map(d => ({
          f90f1_11: d.f90f1_11,
          f90f1_12: d.f90f1_12,
        }))
      );
    }
  };

  const buildPayload = () => ({
    f90f_11: empId,
    f90f_13: desc,
    f90f_21: oldDept,
    f90f_22: oldTitle,
    f90f_23: oldGrade,
    f90f_24: hireDate,
    f90f_31: newDept,
    f90f_32: newTitle,
    f90f_33: newGrade,
    f90f_38: effDate,
    f90f_43: notifyOldMgr,
    details,
  });

  const save = async () => {
    try {
      if (mode === 'add') {
        await pw9204f1Api.create(buildPayload());
        message.success('新增成功');
      } else {
        await pw9204f1Api.update(current!.f90f_01, buildPayload());
        message.success('修改成功');
      }
      const targetId = mode === 'add' ? undefined : current!.f90f_01;
      const data = await loadRecords();
      if (targetId) {
        const idx = data.findIndex((r: F90F) => r.f90f_01 === targetId);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else {
        setCurrentIdx(0); // newest first
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await pw9204f1Api.delete(current.f90f_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || '刪除失敗');
    }
  };

  // Detail row operations
  const addDetailRow = () => {
    setDetails([...details, { f90f1_11: '', f90f1_12: '' }]);
  };

  const removeDetailRow = (idx: number) => {
    setDetails(details.filter((_, i) => i !== idx));
  };

  const updateDetailRow = (idx: number, field: keyof DetailRequest, value: string) => {
    const copy = [...details];
    copy[idx] = { ...copy[idx], [field]: value };
    setDetails(copy);
  };

  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(
      r => r.f90f_01.includes(searchText) || r.f90f_11.includes(searchText)
    );
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    empId, setEmpId,
    desc, setDesc,
    oldDept, setOldDept, oldTitle, setOldTitle, oldGrade, setOldGrade,
    hireDate, setHireDate,
    newDept, setNewDept, newTitle, setNewTitle, newGrade, setNewGrade,
    effDate, setEffDate,
    notifyOldMgr, setNotifyOldMgr,
    details,
    addDetailRow, removeDetailRow, updateDetailRow,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
